import type { ActionContext, Commit, Module } from 'vuex';
import { getCrmRequests } from '../api/api';
import type { Block } from '../interfaces/block';
import { blocksAt, type Configuration } from '../interfaces/configuration';
import type {
    CloseOpportunityPayload,
    FieldValue,
    OpportunityDetail,
    UpdateOpportunityPayload,
} from '../interfaces/opportunityDetail';

/**
 * Nav: **Opportunity** — the wizard / detail page. See docs/CRM_SECTION_CONTRACT.md §6.
 *
 * Addresses as `crm/opportunityDetail/<action>`, state at `state.crm.opportunityDetail`.
 *
 * **This is the seam between the wizard flow and the field renderer.** Values live in a flat
 * `opportunity.values` array keyed by `(block, instance, field)` — no stage, the data
 * follows the opportunity through the pipeline. The field renderer commits `SET_VALUE` as the user types
 * with `{ blockId, instance, field, value }`, and the flow sends the values array on a draft save or an
 * advance. Values travel through here rather than as component events, so the renderer needs no
 * knowledge of the flow and the flow none of the fields.
 *
 * The summary tabs read from the same values array — summarizable blocks display their data inline.
 *
 * `pendingEntries` holds the entries added with `+ Ajouter` that have no answer yet: an entry exists on
 * the server only through its rows, so until one is typed it lives here.
 */
export interface SetValuePayload {
    blockId: string;
    instance: string;
    field: string;
    value: unknown;
}

export interface EntryPayload {
    blockId: string;
    instance: string;
}

export interface OpportunityDetailState {
    opportunity: OpportunityDetail | null;
    pendingEntries: Record<string, string[]>;
    /** Which stage the flow is showing. Configuration-driven: one step per configured stage. */
    currentStageId: string | null;
    loading: boolean;
    error: string | null;
}

const defaultState = (): OpportunityDetailState => ({
    opportunity: null,
    pendingEntries: {},
    currentStageId: null,
    loading: false,
    error: null,
});

/**
 * The only place a write result reaches state. A failed call rejects (the interceptor has already toasted
 * the server's message), so the `catch` is what keeps the stepper from advancing on a failed save.
 *
 * Returns whether the write landed, so the flow can decide whether to move.
 */
async function commitWrite(
    commit: Commit,
    request: () => Promise<OpportunityDetail>,
): Promise<boolean> {
    commit('SET_ERROR', null);
    try {
        commit('SET_OPPORTUNITY', await request());
        return true;
    } catch {
        commit('SET_ERROR', 'crm.opportunityDetail.errors.saveFailed');
        return false;
    }
}

const findValue = (
    values: FieldValue[],
    blockId: string,
    instance: string,
    fieldId: string,
): FieldValue | undefined => values.find((value) =>
    value.block === blockId &&
    value.instance === instance &&
    value.field === fieldId,
);

const answeredEntries = (values: FieldValue[], blockId: string): string[] =>
    [...new Set(values.filter((value) => value.block === blockId).map((value) => value.instance))];

/**
 * A block's entries, stable across typing: the block's own id — its first entry — then the added ones in
 * id order, which is creation order since `newEntryId()` starts with a timestamp.
 */
const sortEntries = (blockId: string, entries: Iterable<string>): string[] => [...new Set(entries)]
    .sort((first, second) => (first === blockId ? -1 : second === blockId ? 1 : first.localeCompare(second)));

type RootGetters = Record<string, unknown>;

/**
 * What a save may send: the answers to blocks present at the opportunity's current stage, for fields the
 * catalogue still has. The server refuses anything else, and the working copy can hold more —
 * the rows of a block since removed from the pipeline stay on the opportunity. Without the
 * configuration or the catalogue loaded, everything is sent and the server decides.
 */
const buildValuesPayload = (opportunity: OpportunityDetail, rootGetters: RootGetters): FieldValue[] => {
    const configuration = (rootGetters['crm/configurationCrm/configurationById'] as
        ((id: string) => Configuration | null) | undefined)?.(opportunity.configuration);
    const catalogue = (rootGetters['crm/advancedConfig/blocks'] as Block[] | undefined) ?? [];
    const stage = configuration?.stages.find((candidate) => candidate.id === opportunity.stage);
    if (!configuration || !stage || !catalogue.length) {
        return opportunity.values.map((value) => ({ ...value }));
    }

    const present = new Set(blocksAt(configuration, stage).map((placed) => placed.block));
    const blocks = new Map(catalogue.map((block) => [block.id, block]));
    return opportunity.values
        .filter((value) => {
            const block = blocks.get(value.block);
            return present.has(value.block)
                && !!block?.fields.some((field) => field.id === value.field)
                && (block.multiple || value.instance === block.id);
        })
        .map((value) => ({ ...value }));
};

type Context = ActionContext<OpportunityDetailState, unknown>;

export const opportunityDetailModule: Module<OpportunityDetailState, unknown> = {
    namespaced: true,
    state: defaultState,
    mutations: {
        /** Reseeds the working copy: the server's values are the new baseline. */
        SET_OPPORTUNITY(state: OpportunityDetailState, opportunity: OpportunityDetail): void {
            if (state.opportunity?.id !== opportunity.id) {
                state.pendingEntries = {};
            } else {
                // A pending entry the server now stores is an ordinary entry.
                state.pendingEntries = Object.fromEntries(Object.entries(state.pendingEntries).map(
                    ([blockId, instances]) => [blockId, instances.filter(
                        (instance) => !answeredEntries(opportunity.values, blockId).includes(instance),
                    )],
                ));
            }
            state.opportunity = { ...opportunity };
        },
        /** The field renderer's write. Upserts by `(blockId, instance, field)`. */
        SET_VALUE(state: OpportunityDetailState, entry: SetValuePayload): void {
            if (!state.opportunity) {
                return;
            }
            const existing = findValue(state.opportunity.values, entry.blockId, entry.instance, entry.field);
            if (existing) {
                existing.value = entry.value;
            } else {
                state.opportunity.values.push({
                    block: entry.blockId,
                    instance: entry.instance,
                    field: entry.field,
                    value: entry.value,
                });
            }
        },
        /**
         * `+ Ajouter <nom du bloc>` — a new, empty entry of a `multiple` block. A block with no entry yet
         * shows its implicit first one, which becomes pending too so it stays on screen.
         */
        ADD_ENTRY(state: OpportunityDetailState, { blockId, instance }: EntryPayload): void {
            const pending = state.pendingEntries[blockId] ?? [];
            const implicitFirst = !pending.length && !answeredEntries(state.opportunity?.values ?? [], blockId).length;
            state.pendingEntries = {
                ...state.pendingEntries,
                [blockId]: [...pending, ...(implicitFirst ? [blockId] : []), instance],
            };
        },
        REMOVE_PENDING_ENTRY(state: OpportunityDetailState, { blockId, instance }: EntryPayload): void {
            state.pendingEntries = {
                ...state.pendingEntries,
                [blockId]: (state.pendingEntries[blockId] ?? []).filter((candidate) => candidate !== instance),
            };
            if (state.opportunity) {
                state.opportunity.values = state.opportunity.values.filter(
                    (value) => value.block !== blockId || value.instance !== instance,
                );
            }
        },
        SELECT_STAGE(state: OpportunityDetailState, stageId: string | null): void {
            state.currentStageId = stageId;
        },
        SET_LOADING(state: OpportunityDetailState, value: boolean): void {
            state.loading = value;
        },
        SET_ERROR(state: OpportunityDetailState, message: string | null): void {
            state.error = message;
        },
    },
    actions: {
        async fetchOpportunity({ commit }: Context, opportunityId: string): Promise<void> {
            commit('SET_LOADING', true);
            try {
                await commitWrite(commit, () => getCrmRequests().opportunityDetail.get(opportunityId));
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async createOpportunity(
            { commit }: Context,
            configurationId: string,
        ): Promise<OpportunityDetail | null> {
            let created: OpportunityDetail | null = null;
            await commitWrite(commit, async () => {
                created = await getCrmRequests().opportunityDetail.createEmpty(configurationId);
                return created;
            });
            return created;
        },
        /** `Enregistrer le brouillon`. */
        async saveDraft(
            { commit, state, rootGetters }: Context,
            payload: UpdateOpportunityPayload = {},
        ): Promise<boolean> {
            const opportunity = state.opportunity;
            if (!opportunity) {
                return false;
            }
            return commitWrite(commit, () => getCrmRequests().opportunityDetail.update(opportunity.id, {
                values: buildValuesPayload(opportunity, rootGetters),
                draft: true,
                ...payload,
            }));
        },
        /**
         * `Valider et passer à l'étape suivante`. Sends the working copy with the call — one request, so
         * there is no saved-but-not-advanced state — and resolves `false` if the server's gate refuses.
         */
        async advance({ commit, state, rootGetters }: Context): Promise<boolean> {
            const opportunity = state.opportunity;
            if (!opportunity) {
                return false;
            }
            return commitWrite(commit, () => getCrmRequests().opportunityDetail.advance(opportunity.id, {
                values: buildValuesPayload(opportunity, rootGetters),
            }));
        },
        /** A pending entry was never saved, so it only leaves the working copy. */
        async deleteEntry({ commit, state }: Context, entry: EntryPayload): Promise<boolean> {
            const opportunity = state.opportunity;
            if (!opportunity) {
                return false;
            }
            // Pending until the server returns it, whatever was typed into it meanwhile.
            if ((state.pendingEntries[entry.blockId] ?? []).includes(entry.instance)) {
                commit('REMOVE_PENDING_ENTRY', entry);
                return true;
            }
            return commitWrite(commit, () => getCrmRequests().opportunityDetail.deleteEntry(
                opportunity.id,
                entry.blockId,
                entry.instance,
            ));
        },
        /** `Terminer l'opportunité` — `Gagnée` or `Perdue`. */
        async close({ commit, state }: Context, payload: CloseOpportunityPayload): Promise<boolean> {
            const opportunityId = state.opportunity?.id;
            if (!opportunityId) {
                return false;
            }
            return commitWrite(commit, () =>
                getCrmRequests().opportunityDetail.close(opportunityId, payload));
        },
    },
    getters: {
        opportunity: (state: OpportunityDetailState): OpportunityDetail | null => state.opportunity,
        /**
         * A block's entries, stored and pending. A block with neither is one empty entry under the block's
         * own id — the entry every block starts with.
         */
        entriesFor: (state: OpportunityDetailState) => (blockId: string): string[] => {
            const entries = sortEntries(blockId, [
                ...answeredEntries(state.opportunity?.values ?? [], blockId),
                ...(state.pendingEntries[blockId] ?? []),
            ]);
            return entries.length ? entries : [blockId];
        },
        isPendingEntry: (state: OpportunityDetailState) => (blockId: string, instance: string): boolean =>
            (state.pendingEntries[blockId] ?? []).includes(instance),
        /** How many of a block's entries the server holds — the last one cannot be deleted. */
        storedEntryCount: (state: OpportunityDetailState) => (blockId: string): number => {
            const pending = state.pendingEntries[blockId] ?? [];
            return answeredEntries(state.opportunity?.values ?? [], blockId)
                .filter((instance) => !pending.includes(instance)).length;
        },
        valueFor: (state: OpportunityDetailState) =>
            (blockId: string, fieldId: string, instance: string): unknown =>
                findValue(state.opportunity?.values || [], blockId, instance, fieldId)?.value,
        currentStageId: (state: OpportunityDetailState): string | null => state.currentStageId,
        loading: (state: OpportunityDetailState): boolean => state.loading,
        error: (state: OpportunityDetailState): string | null => state.error,
    },
};
